import { DateTime } from "luxon";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { MobileDaySection } from "@/domains/appointment/appointments/components/appointments-list/components/mobile-day-section";
import { useUpdateAppointmentServiceWorker } from "@/domains/appointment/appointments/shared/hooks/use-update-appointment-service-worker";
import { useUpdateAppointment } from "@/domains/appointment/appointments/shared/hooks/useUpdateAppointment";
import { sortAppointmentsByStartTime } from "@/domains/appointment/appointments/shared/utils/appointment-utils";
import type { AppointmentBookingStatus, AppointmentOutcome } from "@/gql/graphql";
import { useIsMobile } from "@/hooks/hooks/use-mobile";
import { CenteredSpinner } from "@/lib/components/CenteredSpinner";
import { useDialogRouterSimple as useDialogRouter } from "@/lib/dialog/use-dialog-router-simple";
import { useConfirmationsAppointments } from "../../hooks/use-confirmations-appointments";
import { useNewCalendarUIStore } from "../../store/use-new-calendar-ui-store";
import { DesktopAppointmentTable } from "./desktop-appointment-table";
import { MobileAppointmentList } from "./mobile-appointment-list";

export function ConfirmationsListView() {
  const { t, i18n } = useTranslation("newCalendar");
  const isMobile = useIsMobile();
  const loadMoreRef = useRef<HTMLDivElement>(null);

  // Get expand/collapse state for multi-service appointments
  const { multiServiceExpanded } = useNewCalendarUIStore();

  // Data fetching
  const { appointments, loading, error, hasMore, loadMore, refetch, byDate } =
    useConfirmationsAppointments();

  // Dialog and update handlers
  const { openDialog } = useDialogRouter();
  const { updateAppointment } = useUpdateAppointment();
  const { updateAppointmentServiceWorker } = useUpdateAppointmentServiceWorker();

  // Stable intersection observer callback
  const handleIntersection = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      if (entries[0].isIntersecting) {
        loadMore();
      }
    },
    [loadMore]
  );

  // Stable observer options
  const observerOptions = useMemo(
    () => ({
      threshold: 0.1,
      rootMargin: "200px",
      root: null,
    }),
    []
  );

  // Infinite scroll for load more
  useEffect(() => {
    if (!hasMore) return;

    const observer = new IntersectionObserver(handleIntersection, observerOptions);
    const currentRef = loadMoreRef.current;

    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
      observer.disconnect();
    };
  }, [hasMore, handleIntersection, observerOptions]);

  // Event handlers
  const handleAppointmentClick = (appointmentId: string) => {
    openDialog({ type: "appointment-detail", id: appointmentId });
  };

  const handleStatusUpdate = async (
    appointmentId: string,
    newBookingStatus: string,
    newOutcome?: string | null
  ) => {
    await updateAppointment(appointmentId, {
      bookingStatus: newBookingStatus as AppointmentBookingStatus,
      outcome: newOutcome as AppointmentOutcome | undefined,
    });
  };

  const handleOutcomeUpdate = async (appointmentId: string, newOutcome: string | null) => {
    try {
      const backendOutcome = newOutcome === "" ? null : newOutcome;
      await updateAppointment(appointmentId, {
        outcome: backendOutcome as AppointmentOutcome | undefined,
      });
    } catch (error) {
      console.error("Failed to update appointment outcome:", error);
    }
  };

  const handleServiceWorkerUpdate = async (
    appointmentId: string,
    serviceId: string,
    workerId: string | null
  ) => {
    try {
      await updateAppointmentServiceWorker(appointmentId, serviceId, workerId);
    } catch (error) {
      console.error("Failed to update service worker:", error);
    }
  };

  // Error state
  if (error && !appointments.length) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600 mb-4">
          {t("confirmations.errorLoading")}: {error.message}
        </p>
        <button
          type="button"
          onClick={() => refetch()}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          {t("confirmations.tryAgain")}
        </button>
      </div>
    );
  }

  // Loading state (first load)
  const isFirstLoad = loading && !appointments.length;
  if (isFirstLoad) {
    return <CenteredSpinner />;
  }

  // Empty state
  if (!appointments.length && !loading) {
    return (
      <div className="text-center py-12">
        <div className="mx-auto max-w-md">
          <div className="rounded-full bg-muted p-6 mx-auto w-fit mb-4">
            <svg
              className="w-6 h-6 text-muted-foreground"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <title>No confirmations</title>
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-foreground mb-2">
            {t("confirmations.emptyState.title")}
          </h3>
          <p className="text-muted-foreground">{t("confirmations.emptyState.description")}</p>
        </div>
      </div>
    );
  }

  // Sort dates for consistent display order
  const sortedDates = Object.keys(byDate).sort();

  return (
    <div className="space-y-6">
      {sortedDates.map((dateKey) => {
        const appointmentsForDate = sortAppointmentsByStartTime(byDate[dateKey]);
        const date = DateTime.fromFormat(dateKey, "yyyy-MM-dd");

        return (
          <div key={dateKey} className="space-y-4">
            {/* Mobile: Sticky day section header */}
            <div className="block md:hidden">
              <MobileDaySection
                date={date}
                appointmentCount={appointmentsForDate.length}
                hideAutoOrganize={true} // Hide auto-organize for confirmation requests
                hideCalendar={true} // Hide calendar icon for confirmation requests
              />
            </div>

            {/* Desktop: Date header */}
            <div className="hidden md:block">
              <div className="flex items-center justify-between pb-3 border-b border-border/50">
                <div className="flex items-center gap-3">
                  <h3 className="text-lg font-semibold text-foreground">
                    {date.setLocale(i18n.language).toFormat("EEEE, dd-MM-yyyy")}
                  </h3>
                </div>
              </div>
            </div>

            {/* Appointment list for this date */}
            {isMobile ? (
              <MobileAppointmentList
                appointments={appointmentsForDate}
                currentDate={date}
                onAppointmentClick={handleAppointmentClick}
                onStatusUpdate={handleStatusUpdate}
                onOutcomeUpdate={handleOutcomeUpdate}
                onServiceWorkerUpdate={handleServiceWorkerUpdate}
              />
            ) : (
              <DesktopAppointmentTable
                appointments={appointmentsForDate}
                currentDate={date}
                multiServiceExpanded={multiServiceExpanded}
                onAppointmentClick={handleAppointmentClick}
                onStatusUpdate={handleStatusUpdate}
                onOutcomeUpdate={handleOutcomeUpdate}
                onServiceWorkerUpdate={handleServiceWorkerUpdate}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}
